import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { SelectedItem } from "./DataGridSelectionMachine";
/**
 * Pure model class that represents selected items (rows, columns, cells) in a DataGrid.
 */
export class SelectionRegion {
  constructor() {
    _defineProperty(this, "selectedRowItems", new Map());
    _defineProperty(this, "selectedColumns", new Set());
    _defineProperty(this, "columnsWithSelectedCells", new Map());
    _defineProperty(this, "_selectedRowCount", 0);
    _defineProperty(this, "_isSelectAll", false);
    _defineProperty(this, "_focusCell", null);
    _defineProperty(this, "anchorItem", null);
    _defineProperty(this, "anchorItem2", null);
    _defineProperty(this, "anchorItemGroup", null);
    _defineProperty(this, "suspendChangeNotificationCount", 0);
    _defineProperty(this, "needChangeNotification", false);
    _defineProperty(this, "needFocusChangeNotification", false);
    _defineProperty(this, "onSelectionChange", void 0);
    _defineProperty(this, "onFocusCellChange", void 0);
  } // hi there
  get focusCell() {
    return this._focusCell;
  }
  set focusCell(cell) {
    if (SelectedItem.areEqual(cell, this.focusCell)) {
      return;
    }
    this._focusCell = cell;
    this.anchorItem = this.anchorItem2 = this.focusCell;
    this.onFocusCellChangeInternal();
  }
  isFocusCell(rowId, colId) {
    if (this.focusCell == null) {
      return false;
    }
    return this.focusCell.rowId === rowId && this.focusCell.colId === colId;
  }

  /**
   * Clears the selection region.
   */
  clear() {
    let wasSelectAll = this.isSelectAll;
    let selectedCount = this.selectedRowItems.size + this.selectedColumns.size;
    this._isSelectAll = false;
    this.selectedRowItems.clear();
    this.selectedColumns.clear();
    this.columnsWithSelectedCells.clear();
    this._selectedRowCount = 0;
    this.clearFocusCell();
    if (selectedCount > 0 || wasSelectAll) {
      this.onSelectionChangeInternal();
    }
    return selectedCount > 0 || wasSelectAll;
  }
  clearFocusCell() {
    if (this.focusCell == null) {
      return;
    }
    this.focusCell = null;
  }
  setFocusCell(rowId, colId) {
    if (this.isFocusCell(rowId, colId)) {
      // Already set
      return;
    }
    this.focusCell = new SelectedItem(rowId, colId);
  }
  get isSelectAll() {
    return this._isSelectAll;
  }
  get isEmpty() {
    return !this.isSelectAll && this.selectedRowItems.size === 0 && !this.hasColumnsSelected;
  }
  selectAll() {
    if (this.isSelectAll) {
      return;
    }
    this.clear();
    this._isSelectAll = true;
    this.onSelectionChangeInternal();
  }
  getItemsInRow(rowId) {
    return this.selectedRowItems.get(rowId);
  }
  selectRow(rowId) {
    if (this.getItemsInRow(rowId) === null) {
      // Already selected since null is the value representing a row selection
      return;
    }
    this.selectedRowItems.set(rowId, null);
    this._selectedRowCount++;
    this.onSelectionChangeInternal();
  }
  deselectRow(rowId) {
    if (!this.selectedRowItems.has(rowId)) {
      // Nothing there
      return;
    }
    this.selectedRowItems.delete(rowId);
    this._selectedRowCount = Math.max(0, this._selectedRowCount - 1);
    this.onSelectionChangeInternal();
  }
  isRowSelected(rowId) {
    if (this.isSelectAll) {
      return true;
    }

    // Note the explicit check for null. null means whole row is selected
    return this.getItemsInRow(rowId) === null;
  }
  selectCell(rowId, colId) {
    if (this.isCellSelected(rowId, colId)) {
      // Already selected
      return;
    }
    let rowSet = this.getItemsInRow(rowId);
    if (rowSet == null) {
      rowSet = new Set();
      rowSet.add(colId);
      this.selectedRowItems.set(rowId, rowSet);
    } else {
      rowSet.add(colId);
    }
    let cellCount = this.columnsWithSelectedCells.get(colId);
    if (cellCount === undefined) {
      this.columnsWithSelectedCells.set(colId, 1);
    } else {
      this.columnsWithSelectedCells.set(colId, cellCount + 1);
    }
    this.onSelectionChangeInternal();
  }
  deselectCell(rowId, colId) {
    let rowSet = this.getItemsInRow(rowId);
    if (rowSet == null) {
      return;
    }
    rowSet.delete(colId);
    if (rowSet.size === 0) {
      this.selectedRowItems.delete(rowId);
    }
    let cellCount = this.columnsWithSelectedCells.get(colId);
    if (cellCount !== undefined) {
      this.columnsWithSelectedCells.set(colId, Math.max(0, cellCount - 1));
    }
    this.onSelectionChangeInternal();
  }

  /**
   * Determines if cell is selected either explicitly or via row or column selection.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isSelected(rowId, colId) {
    if (this.isSelectAll) {
      return true;
    }
    if (colId == null) {
      return this.isRowSelected(rowId);
    }
    if (this.isColumnSelected(colId)) {
      return true;
    }
    let rowSet = this.getItemsInRow(rowId);
    if (rowSet === undefined) {
      // Row is not selected
      return false;
    } else if (rowSet === null) {
      // Whole row is selected
      return true;
    }
    return rowSet.has(colId);
  }

  /**
   * Determines if cell is selected explicitly as a selected cell.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isCellSelected(rowId, colId) {
    let rowSet = this.getItemsInRow(rowId);
    if (rowSet == null) {
      return false;
    }
    return rowSet.has(colId);
  }
  selectColumn(colId) {
    if (this.selectedColumns.has(colId)) {
      // Already selected
      return;
    }
    this.selectedColumns.add(colId);
    this.onSelectionChangeInternal();
  }
  deselectColumn(colId) {
    if (!this.selectedColumns.has(colId)) {
      // Not selected
      return;
    }
    this.selectedColumns.delete(colId);
    this.onSelectionChangeInternal();
  }
  isColumnSelected(colId) {
    if (this.isSelectAll) {
      return true;
    }
    return this.selectedColumns.has(colId);
  }
  columnHasSelectedCells(colId) {
    let cellCount = this.columnsWithSelectedCells.get(colId);
    if (cellCount === undefined) {
      return false;
    }
    return cellCount > 0;
  }
  rowHasSelectedCells(rowId) {
    let rowItems = this.selectedRowItems.get(rowId);
    if (rowItems == null) {
      return false;
    }

    // We know that either the row is selected, or that it contains selected cells
    return !this.isRowSelected(rowId);
  }
  get hasRowsSelected() {
    return this._selectedRowCount > 0;
  }
  get selectedRowCount() {
    return this._selectedRowCount;
  }
  get hasColumnsSelected() {
    return this.selectedColumns.size > 0;
  }
  onSelectionChangeInternal() {
    // console.log(`onSelectionChangeInternal`);
    if (this.isChangeNotificationSuspended) {
      this.needChangeNotification = true;
      return;
    }
    if (this.onSelectionChange) {
      this.onSelectionChange();
    }
  }
  onFocusCellChangeInternal() {
    if (this.isChangeNotificationSuspended) {
      this.needFocusChangeNotification = true;
      return;
    }
    if (this.onFocusCellChange) {
      this.onFocusCellChange();
    }
  }

  /**
   * Suspends onSelectionChange notifications until a subsequent resumeChangeNotifications()
   */
  suspendChangeNotifications() {
    if (this.suspendChangeNotificationCount === 0) {
      this.needChangeNotification = false;
      this.needFocusChangeNotification = false;
    }
    this.suspendChangeNotificationCount++;
  }

  /**
   * Resumes onSelectionChange notifications.
   */
  resumeChangeNotifications() {
    let ret = false;
    if (--this.suspendChangeNotificationCount <= 0) {
      this.suspendChangeNotificationCount = 0;
      if (this.needChangeNotification) {
        this.onSelectionChangeInternal();
        ret = true;
      }
      if (this.needFocusChangeNotification) {
        this.onFocusCellChangeInternal();
        ret = true;
      }
    }
    return ret;
  }
  get isChangeNotificationSuspended() {
    return this.suspendChangeNotificationCount > 0;
  }

  /**
   * Returns true if the item is selected, and it is the only item selected.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isOnlyItemSelected(rowId, colId) {
    if (this.isSelectAll) {
      return false;
    }
    if (rowId == null) {
      // Column selection
      if (!this.isColumnSelected(colId)) {
        return false;
      }
    } else if (colId == null) {
      // Row selection
      if (!this.isRowSelected(rowId)) {
        return false;
      }
    } else {
      // Cell selection
      if (!this.isCellSelected(rowId, colId)) {
        return false;
      }
    }

    // OK, we know our item is selected, but is it the only one?
    return !this.isMoreThanOneItemSelected;
  }
  get isMoreThanOneItemSelected() {
    if (this.isSelectAll) {
      return true;
    }
    if (this.selectedRowItems.size + this.selectedColumns.size > 1) {
      return true;
    }
    if (this.selectedRowItems.size === 1) {
      // If we have exactly 1 row item, need to look more closely
      let rowItems = this.selectedRowItems.values().next().value;
      if (rowItems == null) {
        // Row is selected
        return false;
      } else {
        return rowItems.size > 1;
      }
    }

    // 0 items selected
    return false;
  }

  /**
   * Returns array of SelectedItems.
   * NOTE: This API will not work if isSelectAll = true
   * @returns {SelectedItem[]}
   */
  getSelectedItems() {
    let items = [];
    if (this.isSelectAll) {
      // Create a SelectedItem row for each row
      return items;
    }

    // Add any selected rows or cells
    this.selectedRowItems.forEach((value, rowId) => {
      if (value == null) {
        items.push(new SelectedItem(rowId, null));
      } else {
        value.forEach(colId => {
          if (!this.selectedColumns.has(colId)) {
            items.push(new SelectedItem(rowId, colId));
          }
        });
      }
    });

    // Add any selected columns
    this.selectedColumns.forEach(colId => {
      items.push(new SelectedItem(null, colId));
    });
    return items;
  }
}
//# sourceMappingURL=SelectionRegion.js.map
